import { useEffect, useMemo, useState } from 'react'
import { useTelegramBackButton } from '../../hooks/useTelegramBackButton'
import { useCard, useCards } from '../../hooks/useApi'
import { CALCULATOR_RECIPES } from '../../constants/calculatorAccess'
import { calculateCoefficient } from '../../utils/calculator'
import { parseRecipeMetaFromContent } from '../../utils/parseIngredients'
import RecipePicker from './components/RecipePicker'
import ShapeFieldset from './components/ShapeFieldset'
import {
  EMPTY_SHAPE,
  describeShape,
  fromRecipeMeta,
  isShapeFilled,
  toHeight,
  toShape,
} from './shapeValue'
import './ShapeCalculatorPage.css'

/**
 * Пересчёт рецепта под другую форму: по объёму исходной и новой формы считает
 * коэффициент, на который нужно умножить ингредиенты.
 *
 * Исходные размеры можно подставить из рецепта — списком идут те же карточки,
 * для которых доступен калькулятор внутри рецепта. Ручной ввод остаётся: он
 * покрывает рецепты не из приложения.
 */
const ShapeCalculatorPage = () => {
  useTelegramBackButton(true)

  const [recipeId, setRecipeId] = useState<number | null>(null)
  const [original, setOriginal] = useState(EMPTY_SHAPE)
  const [target, setTarget] = useState(EMPTY_SHAPE)
  const [weight, setWeight] = useState('')

  // Названия берём из списка рецептов, а живое с бэка подставляем поверх, если
  // карточка есть в загруженных: у аккаунта без доступа список карточек может
  // не содержать эти id, и выбор рецепта остался бы пустым.
  const { data: cardsData } = useCards()
  const recipeOptions = useMemo(() => {
    const titleById = new Map(
      (cardsData?.cards ?? []).map(card => [card.card_id, card.title])
    )
    return CALCULATOR_RECIPES.map(recipe => ({
      cardId: recipe.cardId,
      title: titleById.get(recipe.cardId) ?? recipe.title,
    })).sort((a, b) => a.title.localeCompare(b.title, 'ru'))
  }, [cardsData])

  const {
    data: recipeData,
    isLoading: isRecipeLoading,
    isError: isRecipeError,
  } = useCard(recipeId)
  const recipeContent = recipeData?.card?.content

  // Что именно удалось вытащить из рецепта. Без этой строки страница молчит:
  // плейсхолдеры полей выглядят как подставленные значения, и понять, сработала
  // подстановка или нет, невозможно.
  const [recipeNote, setRecipeNote] = useState<string | null>(null)

  // Подставляем размеры, когда долетел контент выбранного рецепта. Зависимость
  // от самого контента, а не от разобранной меты: объект меты пересоздавался бы
  // на каждый рендер и затирал бы ручные правки.
  useEffect(() => {
    if (recipeId === null) {
      setRecipeNote(null)
      return
    }
    if (!recipeContent) return

    const meta = parseRecipeMetaFromContent(recipeContent)
    const shape = fromRecipeMeta(meta)
    setOriginal(shape)
    setWeight(meta.originalWeight ? String(meta.originalWeight) : '')
    setRecipeNote(
      isShapeFilled(shape)
        ? `Из рецепта: ${describeShape(shape)}`
        : 'В этом рецепте не указаны форма и высота — заполните их вручную.'
    )
  }, [recipeId, recipeContent])

  const isReady = isShapeFilled(original) && isShapeFilled(target)

  const coefficient = useMemo(() => {
    const originalShape = toShape(original)
    const targetShape = toShape(target)
    if (!originalShape || !targetShape) return 0
    return calculateCoefficient(
      originalShape,
      toHeight(original),
      targetShape,
      toHeight(target)
    )
  }, [original, target])

  const originalWeight = Number(weight) || 0

  // Карточка может не отдать текст: нет доступа или запрос упал.
  const isRecipeUnavailable =
    recipeId !== null && !isRecipeLoading && (isRecipeError || !recipeContent)

  const handleReset = () => {
    setRecipeId(null)
    setRecipeNote(null)
    setOriginal(EMPTY_SHAPE)
    setTarget(EMPTY_SHAPE)
    setWeight('')
  }

  // safe-area-inset на корень не вешаем: шелл уже оборачивает роутинг в этот
  // класс, а повторный перебил бы наш padding-bottom нулём.
  return (
    <div className="shape-calc-page">
      <div className="shape-calc-body">
        <h1 className="shape-calc-title">Калькулятор формы</h1>
        <p className="shape-calc-subtitle">
          Укажите форму из рецепта и форму, в которой печёте, — посчитаем
          коэффициент, на который нужно умножить все ингредиенты.
        </p>

        <RecipePicker
          options={recipeOptions}
          value={recipeId}
          onChange={setRecipeId}
        />

        {isRecipeLoading && <p className="shape-calc-note">Загружаем рецепт…</p>}

        {isRecipeUnavailable && (
          <p className="shape-calc-note warning">
            Не удалось получить текст рецепта — заполните размеры вручную.
          </p>
        )}

        {!isRecipeLoading && recipeNote && (
          <p
            className={`shape-calc-note ${recipeNote.startsWith('Из рецепта') ? 'success' : 'warning'}`}
          >
            {recipeNote}
          </p>
        )}

        <ShapeFieldset
          title="Форма из рецепта"
          value={original}
          onChange={setOriginal}
        />

        <ShapeFieldset title="Ваша форма" value={target} onChange={setTarget} />

        <label className="shape-calc-weight">
          <span>Вес рецепта, г (необязательно)</span>
          <input
            type="number"
            inputMode="decimal"
            value={weight}
            onChange={e => setWeight(e.target.value)}
            placeholder="1200"
          />
        </label>

        {isReady ? (
          <div className="shape-calc-result">
            <div className="shape-calc-coefficient">
              Коэффициент <strong>×{coefficient.toFixed(2)}</strong>
            </div>

            <p className="shape-calc-result-text">
              Умножьте на него количество каждого ингредиента.
            </p>

            {originalWeight > 0 && (
              <div className="shape-calc-weight-result">
                Примерный вес:{' '}
                <strong>~{Math.round(originalWeight * coefficient)} г</strong>
                <span> (исходный: {originalWeight} г)</span>
              </div>
            )}

            <dl className="shape-calc-summary">
              <div>
                <dt>Из рецепта</dt>
                <dd>{describeShape(original)}</dd>
              </div>
              <div>
                <dt>Ваша</dt>
                <dd>{describeShape(target)}</dd>
              </div>
            </dl>

            <p className="shape-calc-hint">
              Для крема и ганаша добавьте +5–10% к рассчитанному количеству,
              особенно для прямоугольных форм.
            </p>

            <button
              type="button"
              className="shape-calc-reset"
              onClick={handleReset}
            >
              Очистить
            </button>
          </div>
        ) : (
          <p className="shape-calc-placeholder">
            Заполните размеры и высоту обеих форм — расчёт появится здесь.
          </p>
        )}
      </div>
    </div>
  )
}

export default ShapeCalculatorPage
